import { defaultCurrency } from '@msrllabs/pt-api/constants';
import {
  Account,
  Exchange,
  Holiday,
  Instrument,
  KycStatus,
  newInstrument,
  newUserPreferences,
  TradingSchedule,
  User,
  UserPreferences,
} from '@msrllabs/pt-api/types';
import { AuthTokens, fetchAuthSession, getCurrentUser as getAuthUser } from 'aws-amplify/auth';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { apiErrorTelemetryFields, unwrapApiError } from '@/api/errors';
import { getKycStatus } from '@/api/kyc';
import { getExchanges, getHolidays, getTradingSchedules, searchInstruments } from '@/api/marketData';
import { getAccounts } from '@/api/trading';
import { getCurrentUser, getUserPreferences, updateCurrentUserSession } from '@/api/user';
import { brokerageAccountTypes } from '@/AuthConfig';
import Loading from '@/components/Loading';
import AppModals from '@/components/Modals/AppModals';
import { AUTH_ROUTES } from '@/Constants';
import { useAsyncEffect } from '@/Hooks';
import { applyAccountLanguage } from '@/i18n';
import { clearSessionExpired, setSessionAuthenticated } from '@/lib/sessionExpiry';
import { emitTelemetry, setObservabilityUserId } from '@/observability';
import { CognitoUser } from '@/Types';
import { registerPushPlayerId } from '@/utils/OneSignalUtils';

import { clearStoredAccountType, readStoredAccountType, resolveAccountType, storeAccountType } from './accountSelection';

interface IPtContext {
  // the account user has chosen, empty string if none
  accountId: string;
  accountType: string;
  accountBalance: number;
  accountCurrency: string;
  accounts: Account[];
  accountLeverage: number;
  accountMarginCallLevel: number;
  accountStopOutLevel: number;

  // logged-in user
  cognitoUser?: CognitoUser; // cognito
  currentUser: User; // AQT
  isAuthLoading: boolean;

  // instrument names etc preloaded information
  exchanges: { [exchangeId: string]: Exchange };
  holidays: { [exchangeId: string]: Array<Holiday> };
  instrumentByKey: { [instrumentKey: string]: Instrument };
  instruments: { [instrumentId: string]: Instrument };
  kycStatus: KycStatus;
  tradingSchedules: { [exchangeId: string]: TradingSchedule };
  userPreferences: UserPreferences;

  // UI actions
  refresh: () => void;
  goToLogin: (options?: { replace?: boolean; sessionExpired?: boolean }) => void;
  goToSignUp: () => void;

  // getters and setters
  getInstrument: (instrumentId: string) => Instrument;
  switchAccount: (accountType: string) => void;
  setAccountBalance: (accountBalance: number) => void;
  setUserPreferences: (userPreferences: UserPreferences) => void;
}

let newPtContext: () => IPtContext = () => ({
  accountId: '',
  // default account type before authenticated account data has loaded
  accountType: 'PAPER',
  accountBalance: 0,
  accountCurrency: defaultCurrency,
  accountLeverage: 0,
  accountMarginCallLevel: 0,
  accountStopOutLevel: 0,
  accounts: [],
  cognitoUser: undefined,
  currentUser: { id: '' } as User,
  isAuthLoading: true,
  exchanges: {},
  holidays: {},
  instrumentByKey: {},
  instruments: {},
  kycStatus: 'NO_APPLICATION',
  tradingSchedules: {},
  userPreferences: {} as UserPreferences,
  getInstrument: (_instrumentId: string) => newInstrument(),

  refresh: () => {},
  setAccountBalance: () => {},
  setUserPreferences: () => {},
  switchAccount: () => {},
  goToLogin: (_options?: { replace?: boolean; sessionExpired?: boolean }) => {},
  goToSignUp: () => {},
});

let defaultAccountType = newPtContext().accountType;

// fire and forget: the tab has already applied the choice, this only tells the backend
let persistSession = (accountType: string) =>
  updateCurrentUserSession(accountType).catch(caught => {
    emitTelemetry('frontend.account.error', {
      operation: 'updateCurrentUserSession',
      ...apiErrorTelemetryFields(unwrapApiError(caught)),
    });
    console.error(caught);
  });
let dummyInstrument = newInstrument();
let dummyUser: User = { id: '' };

const PtContext = createContext(newPtContext());

export function PtContextProvider({ children }: { children: ReactNode }) {
  let [account, setAccount] = useState(() => ({
    accountType: '',
    accountId: '',
    accountLeverage: 0,
    accountMarginCallLevel: 0,
    accountStopOutLevel: 0,
  }));
  let [accountBalance, setAccountBalance] = useState<number>(0);
  let [accounts, setAccounts] = useState<Array<Account>>([]);
  let [accountType, setAccountType] = useState('');
  let [cognitoUser, setCognitoUser] = useState<CognitoUser>();
  let [currentUser, setCurrentUser] = useState<User>(dummyUser);
  let [exchanges, setExchanges] = useState<Array<Exchange>>([]);
  let [kycStatus, setKycStatus] = useState<KycStatus>('NO_APPLICATION');
  let [holidays, setHolidays] = useState<Array<Holiday>>([]);
  let [instruments, setInstruments] = useState<Array<Instrument>>([]);
  let [tradingSchedules, setTradingSchedules] = useState<Array<TradingSchedule>>([]);
  let [userPreferences, setUserPreferences] = useState<UserPreferences>(newUserPreferences);

  let [refresh, setRefresh] = useState(0);
  let [isAuthLoading, setIsAuthLoading] = useState(true);
  let [isAccountLoading, setIsAccountLoading] = useState(false);
  let [isLoading, setIsLoading] = useState(true);

  let navigate = useNavigate();
  let location = useLocation();
  // captured lazily so goToLogin can record the origin page without re-creating the context value on every navigation
  let locationRef = useRef(location);
  locationRef.current = location;

  let exchangeMap = useMemo(() => Object.fromEntries(exchanges.map(exchange => [exchange.id, exchange])), [exchanges]);

  let holidaysMap = useMemo(() => {
    let holidaysMap: { [exchangeId: string]: Array<Holiday> } = {};
    for (let holiday of holidays) {
      if (holidaysMap[holiday.exchangeId] == null) holidaysMap[holiday.exchangeId] = [];
      holidaysMap[holiday.exchangeId].push(holiday);
    }
    return holidaysMap;
  }, [holidays]);

  let instrumentByKey = useMemo(
    () => Object.fromEntries(instruments.map(instrument => [instrument.key, instrument])),
    [instruments]
  );
  let instrumentMap = useMemo(
    () => Object.fromEntries(instruments.map(instrument => [instrument.instrumentId, instrument])),
    [instruments]
  );
  let tradingScheduleMap = useMemo(
    () => Object.fromEntries(tradingSchedules.map(tradingSchedule => [tradingSchedule.exchangeId, tradingSchedule])),
    [tradingSchedules]
  );

  useAsyncEffect(
    async active => {
      setIsAuthLoading(true);
      try {
        let user = await getAuthUser();
        let authUserSession = await fetchAuthSession();
        const cognitoUser: CognitoUser = { ...user, tokens: authUserSession.tokens as AuthTokens };

        if (active()) {
          const authenticatedContext = setObservabilityUserId(cognitoUser.username);
          emitTelemetry('frontend.auth.session', { authenticated: true }, authenticatedContext);
          setIsAccountLoading(true);
          setCognitoUser(cognitoUser);
        }
        let cognitoId = cognitoUser?.username;
        if (cognitoId) {
          const kycStatus = await getKycStatus(cognitoId);
          active() && setKycStatus(kycStatus);
        }
      } catch (e) {
        console.error(e);
        if (active()) {
          setObservabilityUserId();
          clearStoredAccountType(cognitoUser?.username);
          setCognitoUser(undefined); // user not logged in
          setAccounts([]);
          setCurrentUser(dummyUser);
          setAccount({
            accountType: defaultAccountType,
            accountId: '',
            accountLeverage: 0,
            accountMarginCallLevel: 0,
            accountStopOutLevel: 0,
          });
          setAccountType(defaultAccountType);
          setKycStatus('NO_APPLICATION');
        }
      } finally {
        active() && setIsAuthLoading(false);
      }
    },
    [refresh]
  );

  useEffect(() => {
    setSessionAuthenticated(!!cognitoUser);
    clearSessionExpired();
  }, [cognitoUser]);

  useAsyncEffect(
    async active => {
      if (!cognitoUser) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        let now = +new Date();
        const retryOnce = async <T,>(load: () => Promise<T>) => {
          try {
            return await load();
          } catch (err) {
            console.error(err);
            await new Promise(resolve => setTimeout(resolve, 250));
            return load();
          }
        };
        const [exchanges, holidays, instruments, tradingSchedules] = await Promise.allSettled([
          retryOnce(() => getExchanges()),
          retryOnce(() => getHolidays(new Date(now - 360 * 86400 * 1000), new Date(now + 360 * 86400 * 1000))),
          retryOnce(() => searchInstruments('')),
          retryOnce(() => getTradingSchedules()),
        ]);
        if (!active()) return;

        if (exchanges.status === 'fulfilled') setExchanges(exchanges.value);
        else console.error(exchanges.reason);

        if (holidays.status === 'fulfilled') setHolidays(holidays.value);
        else console.error(holidays.reason);

        if (instruments.status === 'fulfilled') setInstruments(instruments.value);
        else console.error(instruments.reason);

        if (tradingSchedules.status === 'fulfilled') setTradingSchedules(tradingSchedules.value);
        else console.error(tradingSchedules.reason);
      } catch (e) {
        console.error(e);
      } finally {
        active() && setIsLoading(false);
      }
    },
    [cognitoUser?.username]
  );

  useAsyncEffect(
    async active => {
      if (cognitoUser != null) {
        setIsAccountLoading(true);
        try {
          let userPromise = getCurrentUser();
          let accountsPromise = getAccounts();

          let user = await userPromise;
          user.cognitoId = cognitoUser?.username;
          if (active()) {
            setAccountType(user.session ?? defaultAccountType);
            setCurrentUser(user);
            applyAccountLanguage(user.language);
          }

          let accounts = await accountsPromise;
          active() && setAccounts(accounts);
        } catch (e) {
          console.error(e);
        } finally {
          active() && setIsAccountLoading(false);
        }

        await registerPushPlayerId(cognitoUser.username as string);
      } else {
        setIsAccountLoading(false);
        setCurrentUser(dummyUser);
      }
    },
    [cognitoUser]
  );

  useAsyncEffect(async () => {
    if (accounts.length === 0) {
      setAccount({ accountType, accountId: '', accountLeverage: 0, accountMarginCallLevel: 0, accountStopOutLevel: 0 });
      return;
    }

    const cognitoId = cognitoUser?.username;
    if (cognitoId != null) {
      const resolved = resolveAccountType({
        accounts,
        accountType,
        storedAccountType: readStoredAccountType(cognitoId),
        liveAccountTypes: brokerageAccountTypes(),
      });
      if (resolved.persist) storeAccountType(cognitoId, resolved.accountType);
      if (resolved.accountType !== accountType) {
        // returning without an accountId holds the loading gate closed, so no wrong account paints
        setAccountType(resolved.accountType);
        if (resolved.syncSession) persistSession(resolved.accountType);
        return;
      }
    }

    const account = accounts.find(account => account.accountType === accountType);
    if (account) {
      setAccount({
        accountType,
        accountId: account.id,
        accountLeverage: account.leverage,
        accountMarginCallLevel: account.marginCallLevel,
        accountStopOutLevel: account.stopOutLevel,
      });
    } else {
      setAccountType(accounts[0].accountType);
    }
  }, [accounts, accountType, cognitoUser?.username]);

  useAsyncEffect(async () => {
    if (cognitoUser != null) {
      getUserPreferences(cognitoUser.username as string).then(setUserPreferences);
    }
  }, [cognitoUser]);

  const accountCurrency =
    accounts.find(a => a.accountType === account.accountType && a.id === account.accountId)?.currency ?? defaultCurrency;

  let ptContext = useMemo(
    () => ({
      accountId: account.accountId,
      accountType: account.accountType,
      accountBalance,
      accountCurrency,
      accountLeverage: account.accountLeverage,
      accountMarginCallLevel: account.accountMarginCallLevel,
      accountStopOutLevel: account.accountStopOutLevel,
      accounts,
      cognitoUser,
      currentUser,
      isAuthLoading,
      exchanges: exchangeMap,
      holidays: holidaysMap,
      instrumentByKey,
      instruments: instrumentMap,
      kycStatus,
      tradingSchedules: tradingScheduleMap,
      userPreferences,
      refresh: () => setRefresh(r => r + 1),
      goToLogin: (options?: { replace?: boolean; sessionExpired?: boolean }) => {
        let { pathname, search } = locationRef.current;
        navigate(AUTH_ROUTES.login, {
          replace: options?.replace,
          state: { from: `${pathname}${search}`, sessionExpired: options?.sessionExpired },
        });
      },
      goToSignUp: () => navigate(AUTH_ROUTES.signup),
      getInstrument: (instrumentId: string) => instrumentMap[instrumentId] ?? dummyInstrument,
      switchAccount: (nextAccountType: string) => {
        if (!nextAccountType) return;
        const cognitoId = cognitoUser?.username;
        // stored before the state change, so the resolution effect re-runs onto the new choice
        if (cognitoId != null) storeAccountType(cognitoId, nextAccountType);
        const nextAccount = accounts.find(account => account.accountType === nextAccountType);
        setAccountType(nextAccountType);
        setAccount({
          accountType: nextAccountType,
          accountId: nextAccount?.id ?? '',
          accountLeverage: nextAccount?.leverage ?? 0,
          accountMarginCallLevel: nextAccount?.marginCallLevel ?? 0,
          accountStopOutLevel: nextAccount?.stopOutLevel ?? 0,
        });
        if (nextAccountType !== accountType) persistSession(nextAccountType);
      },
      setAccountBalance,
      setUserPreferences,
    }),
    [
      account,
      accounts,
      accountBalance,
      accountType,
      accountCurrency,
      cognitoUser,
      currentUser,
      isAuthLoading,
      exchangeMap,
      holidaysMap,
      instrumentByKey,
      instrumentMap,
      kycStatus,
      tradingScheduleMap,
      userPreferences,
      navigate,
    ]
  );

  return (
    <PtContext.Provider value={ptContext}>
      {!accountType ||
      isLoading ||
      isAuthLoading ||
      isAccountLoading ||
      (cognitoUser && accounts.length > 0 && !account.accountId) ? (
        <Loading />
      ) : (
        children
      )}
      <AppModals cognitoId={currentUser.cognitoId} />
    </PtContext.Provider>
  );
}

export const usePtContext = () => {
  return useContext(PtContext);
};
